<template>
  <l-marker
    v-bind="$attrs"
    :options="{ ...options, interactive: interactive }"
  >
    <l-icon
      :class-name="className"
      :icon-url="getIcon"
      :shadow-url="markerShadow"
      :icon-size="iconSize"
      :icon-anchor="iconAnchor"
      :popup-anchor="popupAnchor"
      :shadow-size="shadowSize"
    />
    <slot />
  </l-marker>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { MarkerOptions } from 'leaflet';
import { LMarker, LIcon } from '@vue-leaflet/vue-leaflet';
import { Station } from '@/stores/station/station-model';
import { MarkerStatus } from '@/util/station';
import blueMarker from '@/assets/pin/marker-midnight.png';
import lightBlueMarker from '@/assets/pin/marker-lime.png';
import greyMarker from '@/assets/pin/marker-grey.png';
import redMarker from '@/assets/pin/marker-red.png';
import markerShadow from '@/assets/pin/marker-shadow.png';

export type IconType = 'blue' | 'light-blue' | 'grey' | 'red';
export type AnimationType = '' | 'bounce';

interface ExtendedMarkerOptions extends MarkerOptions {
  station: Station;
  status: MarkerStatus;
}

export default defineComponent({
  name: 'MapMarker',
  components: {
    LMarker,
    LIcon,
  },
  props: {
    icon: { type: String as () => IconType, required: true },
    animation: { type: String as () => AnimationType, default: '' },
    interactive: { type: Boolean, default: true },
    scaleFactor: { type: Number, default: 1 },
    options: {
      type: Object as () => ExtendedMarkerOptions,
      default: () => ({} as ExtendedMarkerOptions),
    },
  },
  setup(props) {
    const iconSize = computed<[number, number]>(() => [
      25 * props.scaleFactor,
      41 * props.scaleFactor,
    ]);

    const iconAnchor = computed<[number, number]>(() => [
      12 * props.scaleFactor,
      41 * props.scaleFactor,
    ]);

    const popupAnchor = computed<[number, number]>(() => [
      1 * props.scaleFactor,
      -34 * props.scaleFactor,
    ]);

    const shadowSize = computed<[number, number]>(() => [
      41 * props.scaleFactor,
      41 * props.scaleFactor,
    ]);

    const getIcon = computed<string>(() => {
      switch (props.icon) {
        case 'blue':
          return blueMarker;
        case 'light-blue':
          return lightBlueMarker;
        case 'grey':
          return greyMarker;
        case 'red':
          return redMarker;
        default:
          return greyMarker;
      }
    });

    const className = computed<string>(() => {
      const classAttrs: string[] = [];
      if (props.animation) {
        classAttrs.push(`zamg-marker-animation-${props.animation}`);
      }
      return classAttrs.join(' ');
    });

    return {
      markerShadow,
      iconSize,
      iconAnchor,
      popupAnchor,
      shadowSize,
      getIcon,
      className,
    };
  },
});
</script>

<style scoped></style>
